import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE, API_HOST } from "config/api";

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  if (path.startsWith("blob")) return path;
  return `${API_HOST}/storage/${path}`;
};

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const ContributorCell = ({ row }) => {
  const { memberName, memberId, memberPhoto, isGroup, contributorName } = row.original;
  if (isGroup) {
    return (
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: "info.light" }}>
          <GroupsIcon fontSize="small" />
        </Avatar>
        <Box>
          <Typography display="block" variant="button" fontWeight={600}>
            {contributorName || "Group contribution"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            No specific member
          </Typography>
        </Box>
      </Box>
    );
  }
  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Avatar
        src={getImageUrl(memberPhoto)}
        sx={{ width: 40, height: 40 }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "";
        }}
      >
        {!memberPhoto && <PersonIcon fontSize="small" />}
      </Avatar>
      <Box>
        <Typography display="block" variant="button" fontWeight={600}>
          {memberName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ID: {memberId}
        </Typography>
      </Box>
    </Box>
  );
};
ContributorCell.propTypes = { row: PropTypes.shape({ original: PropTypes.object }).isRequired };

const ProjectCell = ({ value }) =>
  value ? (
    <Chip size="small" label={value} sx={{ bgcolor: "ink.100" }} />
  ) : (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  );
ProjectCell.propTypes = { value: PropTypes.string };
ProjectCell.defaultProps = { value: "" };

const StatusCell = ({ value }) => <StatusChip value={value} />;
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <Box display="flex" justifyContent="center" gap={1}>
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Box>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useContributionData() {
  const { user, church } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isGroupContribution, setIsGroupContribution] = useState(false);
  const [contributorName, setContributorName] = useState("");

  const [paymentData, setPaymentData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      {
        Header: "Contributor",
        accessor: "memberName",
        align: "left",
        width: "28%",
        Cell: ContributorCell,
      },
      {
        Header: "Project",
        accessor: "projectName",
        align: "center",
        width: "15%",
        Cell: ProjectCell,
      },
      {
        Header: "Amount (GHS)",
        accessor: "amount",
        align: "center",
        width: "12%",
        Cell: SerialCell,
      },
      { Header: "Method", accessor: "method", align: "center", width: "10%", Cell: SerialCell },
      {
        Header: "Payment Date",
        accessor: "paymentDate",
        align: "center",
        width: "12%",
        Cell: SerialCell,
      },
      { Header: "Status", accessor: "status", align: "center", width: "8%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "15%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const fetchProjects = useCallback(async () => {
    if (!church?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/projects`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const result = await response.json();
      setProjects(Array.isArray(result.data) ? result.data : []);
    } catch (err) {
      console.error("Fetch projects failed:", err);
    }
  }, [church?.id]);

  const searchMembers = useCallback(
    async (query) => {
      if (!church?.id || !query.trim()) {
        setFilteredMembers([]);
        return;
      }
      setIsLoadingMembers(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;
        const response = await fetch(
          `${API_BASE}/members/${church.id}/search?query=${encodeURIComponent(query)}`,
          { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();
        setFilteredMembers(result.data || []);
      } catch (err) {
        console.error("Member search failed:", err);
        setFilteredMembers([]);
      } finally {
        setIsLoadingMembers(false);
      }
    },
    [church?.id]
  );

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredMembers([]);
      setShowMemberDropdown(false);
      return;
    }
    const timer = setTimeout(() => searchMembers(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchMembers]);

  const handleMemberSelect = (member) => {
    setSelectedPayment((prev) => ({
      ...prev,
      member_id: member.id,
      member_name: `${member.first_name} ${member.surname}`,
      memberId: member.memberID,
      memberPhoto: member.photo,
    }));
    setSearchTerm(`${member.first_name} ${member.surname} (${member.memberID})`);
    setShowMemberDropdown(false);
  };

  const handleEdit = useCallback((payment) => {
    const raw = payment.originalData || {};
    setSelectedPayment({
      id: payment.id,
      member_id: raw.member_id || "",
      member_name: payment.memberName || "",
      memberId: payment.memberId || "",
      memberPhoto: payment.memberPhoto || "",
      amount: raw.amount || "",
      method: raw.method || "manual",
      reference: raw.reference || "",
      paymentDate: raw.payment_date
        ? new Date(raw.payment_date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      project_id: raw.project_id || "",
    });
    setIsGroupContribution(!!payment.isGroup);
    setContributorName(payment.isGroup ? payment.contributorName || "" : "");
    setSearchTerm(payment.isGroup ? "" : payment.memberName || "");
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((payment) => {
    setSelectedPayment(payment);
    setDeleteModalOpen(true);
  }, []);

  const handleAddPayment = useCallback(() => {
    setSelectedPayment({
      id: null,
      member_id: "",
      member_name: "",
      memberId: "",
      memberPhoto: "",
      type: "contribution",
      amount: "",
      method: "manual",
      reference: "",
      paymentDate: new Date().toISOString().split("T")[0],
      status: "active",
      project_id: "",
    });
    setIsGroupContribution(false);
    setContributorName("");
    setSearchTerm("");
    setFilteredMembers([]);
    setEditModalOpen(true);
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      setPaymentData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/payments/members/${church.id}?type=contribution`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const payments = result.data || result.payments || result;
      const sorted = [...payments].sort(
        (a, b) => new Date(b.payment_date) - new Date(a.payment_date)
      );

      setPaymentData((prev) => ({
        ...prev,
        rows: sorted.map((pay, index) => ({
          id: pay.id,
          serial: index + 1,
          isGroup: !pay.member_id,
          contributorName: pay.meta?.contributor_name || "",
          memberId: pay.member?.memberID || "N/A",
          memberName: pay.member ? `${pay.member.first_name} ${pay.member.surname}` : "",
          memberPhoto: pay.member?.photo || "",
          projectName: pay.project?.name || "",
          amount: `GHS ${parseFloat(pay.amount).toLocaleString()}`,
          method: pay.method,
          reference: pay.reference,
          paymentDate: new Date(pay.payment_date).toLocaleDateString(),
          status: pay.status === "success" ? "active" : "inactive",
          originalData: pay,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setPaymentData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [user, church]);

  const handleEditSubmit = useCallback(async () => {
    try {
      if (isGroupContribution && !contributorName.trim()) {
        throw new Error("Describe who/what this group contribution is for.");
      }
      if (!isGroupContribution && !selectedPayment?.member_id) {
        throw new Error("Pick a member to record this contribution for.");
      }
      if (!selectedPayment?.amount || Number(selectedPayment.amount) <= 0) {
        throw new Error("Enter a contribution amount greater than zero.");
      }

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedPayment.id
        ? `${API_BASE}/payments/${selectedPayment.id}`
        : `${API_BASE}/payments`;
      const method = selectedPayment.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: isGroupContribution ? null : selectedPayment.member_id,
          contributor_name: isGroupContribution ? contributorName.trim() : null,
          type: "contribution",
          amount: selectedPayment.amount,
          method: selectedPayment.method || "manual",
          reference: selectedPayment.reference || null,
          payment_date: selectedPayment.paymentDate,
          project_id: selectedPayment.project_id || null,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Contribution ${selectedPayment.id ? "updated" : "recorded"}`,
        icon: "success",
      });

      fetchPayments();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Contribution save failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  }, [selectedPayment, isGroupContribution, contributorName, fetchPayments]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/payments/${selectedPayment.id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({ title: "Success!", text: "Contribution deleted", icon: "success" });
      fetchPayments();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  }, [selectedPayment, fetchPayments]);

  const handlePrint = useCallback(() => window.print(), []);

  useEffect(() => {
    if (church?.id) {
      fetchPayments();
      fetchProjects();
    }
  }, [church?.id, fetchPayments, fetchProjects]);

  return {
    ...paymentData,
    projects,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayment,
    setSelectedPayment,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddPayment,
    searchTerm,
    setSearchTerm,
    filteredMembers,
    isLoadingMembers,
    showMemberDropdown,
    setShowMemberDropdown,
    handleMemberSelect,
    isGroupContribution,
    setIsGroupContribution,
    contributorName,
    setContributorName,
  };
}
